import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Deck from "../../shared/api/DeckApi";
import { CardDeckPreview } from "../../shared/interfaces/GameLogicTypes";
import CardDeck from "../../components/CardDeck/CardDeck";
import CardDeckViewer from "../../components/CardDeckViewer/CardDeckViewer";
import { convertBackendToFrontendDecks } from "../../shared/helper/functions";

export default function CreatorProfilePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { name } = useParams<{ name: string }>();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [creatorName, setCreatorName] = useState("");
  const [bio, setBio] = useState("");
  const [globalRank, setGlobalRank] = useState(0);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [decks, setDecks] = useState<CardDeckPreview[]>([]);
  const [previewDeckId, setPreviewDeckId] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await Deck.getCreatorProfile(decodeURIComponent(name));
        if (!res) {
          setNotFound(true);
          return;
        }
        setCreatorName(res.name);
        setBio(res.bio ?? "");
        setGlobalRank(res.globalRank);
        setGlobalTotal(res.globalTotal);
        setDecks(convertBackendToFrontendDecks(res.decks));
      } catch (e) {
        console.error("Failed to load creator profile", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [name]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" sx={{ pt: 8 }}>
            <Typography variant="h4">{t("creator_not_found")}</Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={() => history.goBack()}
            >
              {t("creator_back_to_leaderboard")}
            </Button>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          {/* Back button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
            sx={{ color: "text.primary", alignSelf: "flex-start" }}
          >
            {t("creator_back_to_leaderboard")}
          </Button>

          {/* Header */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <Typography variant="h4" fontWeight="bold">
                {creatorName}
              </Typography>
              {globalRank > 0 && (
                <Chip
                  icon={<EmojiEventsIcon sx={{ color: "#ffc107" }} />}
                  label={`#${globalRank} / ${globalTotal}`}
                  variant="outlined"
                />
              )}
            </Stack>
            {bio && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {bio}
              </Typography>
            )}
          </Paper>

          {/* Decks grid */}
          {decks.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">{t("creator_no_decks")}</Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                {t("creator_decks_title", { count: decks.length })}
              </Typography>
              <Grid
                container
                spacing={2}
                columns={{ xs: 2, sm: 3, md: 3, lg: 4 }}
              >
                {decks.map((deck, index) => (
                  <Grid item xs={1} key={"creator-deck-" + index}>
                    <CardDeck
                      featured={deck.featured}
                      title={deck.name}
                      questions={deck.questionCardCount}
                      answers={deck.answerCardCount}
                      categories={deck.categoriesCount}
                      playCount={deck.playCount}
                      language={deck.language}
                      id={deck.id}
                      onPreview={() => setPreviewDeckId(deck.id)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </Stack>
      </Container>

      <Backdrop open={previewDeckId !== ""} sx={{ overflow: "scroll", zIndex: 1200 }}>
        <CardDeckViewer
          previewDeckId={previewDeckId}
          setClose={() => setPreviewDeckId("")}
        />
      </Backdrop>
    </Box>
  );
}
